import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import wretch from 'wretch';
import moment from 'moment-timezone';

// MUI imports for confirmation dialog and checkbox UI
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { IWOS_COMMANDS } from '../../../../utils/constants';

import CommandHistory from '../../../../components/CommandHistory';

moment.tz.setDefault('UTC');

// Module IDs confirmed from docs/phase2.md
const IWOS_MODULES = [
	{ id: 'CT', name: 'Ceilometer' },
	{ id: 'WI', name: 'Wind' },
	{ id: 'TPH', name: 'TPH (Temperature Pressure Humidity)' },
	{ id: 'LT', name: 'Lightning' },
	{ id: 'Vis/PW', name: 'Present Weather/Visibility' },
];

const IWOSCommands = ({ device }) => {
	const { username, token } = JSON.parse(sessionStorage.getItem('selfData'));
	const [reportingMode, setReportingMode] = useState('');
	const [gps, setGps] = useState('');
	const [module, setModule] = useState('');
	const [other, setOther] = useState('');
	const [isSending, setSending] = useState(false);
	const [disableSend, setDisableSend] = useState(false);
	const [confirmType, setConfirmType] = useState(null);

	// Secondary inputs
	const [reportingModeRate, setReportingModeRate] = useState('5');
	const [gpsElevation, setGpsElevation] = useState('');
	const [selectedModules, setSelectedModules] = useState([]);

	// Confirmation dialog state
	const [confirmDialog, setConfirmDialog] = useState({
		open: false,
		title: '',
		message: '',
		onConfirm: null,
	});

	const [errorFields, setErrorFields] = useState([]);
	const [shouldGetHistory, setShouldGetHistory] = useState(false);

	const {
		REPORTING,
		GPS,
		MODULES,
		OTHER,
	} = IWOS_COMMANDS;

	// Disable the send button if a request is in progress, or all values are blank,
	// or if modules are selected but no module IDs chosen
	useEffect(() => {
		const hasModuleAction = module === MODULES.DISABLE || module === MODULES.ENABLE;
		const moduleSelectionInvalid = hasModuleAction && selectedModules.length === 0;

		if (
			isSending ||
			(reportingMode === '' && gps === '' && module === '' && other === '') ||
			moduleSelectionInvalid
		) {
			setDisableSend(true);
		} else {
			setDisableSend(false);
		}

		let eFields = errorFields;
		setErrorFields(eFields);
	}, [isSending, gps, reportingMode, module, other, selectedModules, MODULES, errorFields]);

	// Handler for module checkbox changes
	const handleModuleChange = (moduleId) => {
		setSelectedModules(prev =>
			prev.includes(moduleId)
				? prev.filter(id => id !== moduleId)
				: [...prev, moduleId]
		);
	};

	// Show confirmation dialog
	const showConfirmDialog = (title, message, onConfirm) => {
		setConfirmDialog({ open: true, title, message, onConfirm });
	};

	// Close confirmation dialog
	const closeConfirmDialog = () => {
		setConfirmDialog(prev => ({ ...prev, open: false }));
	};

	const submitCommand = async (e, commandOverride = null) => {
		if (e) e.preventDefault();
		setSending(true);

		let commandsToSend = [];

		if (commandOverride) {
			// Direct command (e.g., reset)
			commandsToSend = [commandOverride];
		} else {
			// Build commands from form state
			let reportingModeFormatted = reportingMode;
			if (reportingMode === REPORTING.RATE) {
				reportingModeFormatted += reportingModeRate;
			}

			let gpsFormatted = gps;
			if (gps === GPS.SET_ELEV) {
				gpsFormatted += gpsElevation;
			}

			let moduleFormatted = module;
			if ((module === MODULES.DISABLE || module === MODULES.ENABLE) && selectedModules.length > 0) {
				moduleFormatted += selectedModules.join(',');
			}

			[reportingModeFormatted, gpsFormatted, moduleFormatted, other].forEach((command) => {
				if (command !== '') {
					commandsToSend.push(command);
				}
			});
		}

		let options = {
			method: 'IWOSCommands',
			params: {},
			timeout: 5000,
		};

		options['params']['DestinationIMEI'] = [device.imei];
		options['params']['DeviceType'] = ['IWOS'];
		options['params']['Command'] = [commandsToSend.join(' ')];
		options['params']['Issuer'] = [username];
		options['params']['Modem'] = [device.modem];
		options['params']['CmdSendDate'] = moment().valueOf();

		await wretch(`${process.env.REACT_APP_UTIL_API_URL}/unit/commands/send`)
			.auth(`Bearer ${token}`)
			.post({
				deviceId: device.id,
				options: options,
			})
			.error(500, (error) => {
				console.log(error);
				setConfirmType('error');
			})
			.res(() => {
				setConfirmType('success');
				setShouldGetHistory(true);
			})
			.finally(() => {
				setSending(false);
			});

		setTimeout(() => {
			setConfirmType(null);
		}, 7000);
	};

	// Handle form submit with confirmation for module actions and reset
	const handleSubmitWithConfirmation = (e) => {
		e.preventDefault();

		// Check if Reset Unit needs confirmation
		if (other === OTHER.RESET) {
			showConfirmDialog(
				'Reset Unit?',
				'Are you sure? The connection will temporarily drop and the unit will restart.',
				() => {
					closeConfirmDialog();
					submitCommand(e);
				}
			);
			return;
		}

		// Check if module enable/disable needs confirmation
		if ((module === MODULES.DISABLE || module === MODULES.ENABLE) && selectedModules.length > 0) {
			const action = module === MODULES.ENABLE ? 'Enable' : 'Disable';
			const moduleNames = selectedModules
				.map(id => IWOS_MODULES.find(m => m.id === id)?.name || id)
				.join(', ');
			showConfirmDialog(
				`${action} Modules?`,
				`Are you sure you want to ${action.toLowerCase()}: ${moduleNames}?`,
				() => {
					closeConfirmDialog();
					submitCommand(e);
				}
			);
			return;
		}

		// No confirmation needed for other commands
		submitCommand(e);
	};

	return (
		<>
			<div className="command-form">
				{/* Task 2.1: Reporting Mode - Removed SPECI option */}
				<div className="command-input">
					<span>Reporting Mode:&nbsp;&nbsp;</span>
					<select onChange={(e) => setReportingMode(e.target.value)}>
						<option value="">No command</option>
						<option value={REPORTING.RATE}>Transmit Module Frequency</option>
					</select>
					{reportingMode === REPORTING.RATE && (
						<>
							<div></div>
							<div className="command-input sub-command">
								<span>Rate:&nbsp;&nbsp;</span>
								<select onChange={(e) => setReportingModeRate(e.target.value)}>
									<option value="5">Every 5 minutes</option>
									<option value="10">Every 10 minutes</option>
									<option value="60">Every 60 minutes</option>
								</select>
							</div>
						</>
					)}
				</div>

				{/* Task 2.2: GPS - Removed 5 options, kept Disable GPS, Enable GPS, Set Elevation */}
				<div className="command-input">
					<span>GPS:&nbsp;&nbsp;</span>
					<select onChange={(e) => setGps(e.target.value)}>
						<option value="">No command</option>
						<option value={GPS.DISABLE}>Disable GPS</option>
						<option value={GPS.ENABLE}>Enable GPS</option>
						<option value={GPS.SET_ELEV}>Set Elevation</option>
					</select>
					{gps === GPS.SET_ELEV && (
						<>
							<div></div>
							<div className="command-input sub-command">
								<span>Elevation (m):&nbsp;&nbsp;</span>
								<input
									type="text"
									placeholder="meters above sea level"
									className={errorFields.includes('gpsElevation') ? 'error' : ''}
									value={gpsElevation}
									onChange={(e) => setGpsElevation(e.target.value)}
								/>
							</div>
						</>
					)}
				</div>

				{/* Task 2.3: Enable/Disable Modules - Checkbox UI with friendly names */}
				<div className="command-input">
					<span>Enable/Disable Modules:&nbsp;&nbsp;</span>
					<select onChange={(e) => {
						setModule(e.target.value);
						setSelectedModules([]);
					}}>
						<option value="">No command</option>
						<option value={MODULES.DISABLE}>Disable</option>
						<option value={MODULES.ENABLE}>Enable</option>
					</select>
					{(module === MODULES.DISABLE || module === MODULES.ENABLE) && (
						<>
							<div></div>
							<div className="command-input sub-command">
								<span>Select Modules:&nbsp;&nbsp;</span>
								<FormGroup row>
									{IWOS_MODULES.map((mod) => (
										<FormControlLabel
											key={mod.id}
											control={
												<Checkbox
													checked={selectedModules.includes(mod.id)}
													onChange={() => handleModuleChange(mod.id)}
													size="small"
												/>
											}
											label={mod.name}
											sx={{ marginRight: 2 }}
										/>
									))}
								</FormGroup>
							</div>
						</>
					)}
				</div>

				{/* Task 2.4: Reset Unit - Dropdown with No command / Reset Unit */}
				<div className="command-input">
					<span>Reset Unit:&nbsp;&nbsp;</span>
					<select onChange={(e) => setOther(e.target.value)}>
						<option value="">No command</option>
						<option value={OTHER.RESET}>Reset Unit</option>
					</select>
				</div>

				{/* Send button for other commands */}
				<div className="command-input">
					<span></span>
					<div className="with-confirm">
						<input
							type="submit"
							value="Send"
							disabled={disableSend}
							onClick={(e) => handleSubmitWithConfirmation(e)}
						/>
						<div
							className={`confirmation-message success ${confirmType === 'success' ? 'show' : ''}`}>
							The command has been successfully queued.
						</div>
						<div className={`confirmation-message error ${confirmType === 'error' ? 'show' : ''}`}>
							Sorry, there was an error queuing the command.
						</div>
					</div>
				</div>
			</div>

			{/* Confirmation Dialog */}
			<Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
				<DialogTitle>{confirmDialog.title}</DialogTitle>
				<DialogContent>
					<DialogContentText>{confirmDialog.message}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeConfirmDialog}>Cancel</Button>
					<Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>

			<CommandHistory
				device={device}
				shouldGetHistory={shouldGetHistory}
				setShouldGetHistory={setShouldGetHistory}
			/>
		</>
	);
};

IWOSCommands.propTypes = {
	device: PropTypes.object.isRequired,
};

export default IWOSCommands;
